import { Divider, VStack, HStack, Spacer, Stack } from '@chakra-ui/layout';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Button,
  Tag,
  TagLabel,
  TagLeftIcon,
  DrawerFooter,
  Skeleton,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  date as commonDate,
  finleyAssert,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank, CreditAgreement } from '@finley-technologies/finley-types';

import {
  useGetCreditFacilityMetricsQuery,
  useGetInvoiceByIdQuery,
  InvoiceConsolidatedDisbursementFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { MarkAsDisbursedButton } from 'packages/invoices/ui/consolidatedDisbursements/MarkAsDisbursedButton';
import { TaskDetails } from 'packages/tasks/ui/TaskEntityDetails';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommas,
  formatCurrencyAmount,
  percentageToPercentageLabel,
  truncateUuid,
} from '~/packages/common-frontend/utils';
import { DisbursementNoticeDownloadButton } from '~/packages/invoices/ui/disbursements/InvoiceDisbursementDownloadButton';
import { getBankAccountDisplay } from '~/packages/invoices/ui/utils';

export const InvoiceConsolidatedDisbursementDrawer = ({
  isOpen,
  consolidatedDisbursement,
  facilityMechanics,
  onToggle,
  taskId,
}: {
  isOpen: boolean;
  consolidatedDisbursement: InvoiceConsolidatedDisbursementFragmentGQLType;
  facilityMechanics: CreditAgreement.DomainEntity.FacilityMechanicsWithEntities;
  onToggle: (isOpen: boolean) => void;
  taskId?: string;
}) => {
  const lenderCommitmentAmount = useMemo(() => {
    const capitalProviderEntity = facilityMechanics.parties.capitalProviders.find(
      (capProvider) =>
        capProvider.entity.id ===
        consolidatedDisbursement.capitalProviderOrganization.id,
    );

    finleyAssert(
      capitalProviderEntity,
      `[InvoiceConsolidatedDisbursementDrawer] Missing capitalProviderEntity for ${_.startCase(
        consolidatedDisbursement.capitalProviderOrganization.name,
      )}`,
    );

    return capitalProviderEntity.commitmentAmount;
  }, [
    consolidatedDisbursement.capitalProviderOrganization.id,
    consolidatedDisbursement.capitalProviderOrganization.name,
    facilityMechanics.parties.capitalProviders,
  ]);

  const invoiceQuery = useGetInvoiceByIdQuery({
    variables: {
      invoiceId: consolidatedDisbursement.invoiceId,
    },
  });

  const invoiceLoanIds = useMemo(() => {
    if (invoiceQuery.data?.getInvoiceById) {
      return invoiceQuery.data.getInvoiceById.invoiceComponentsWithInvoiceItems
        .map(({ invoiceComponent }) => invoiceComponent)
        .map((c) => c.finleyLoanId)
        .filter(commonTypeguards.isNotNullOrUndefined);
    }
    return null;
  }, [invoiceQuery.data?.getInvoiceById]);

  const reportDownload = useMemo(() => {
    return invoiceQuery.data?.getInvoiceById.invoiceReports?.find(
      (download) =>
        download.capitalProviderId ===
          consolidatedDisbursement.capitalProviderOrganization.id &&
        download.reportType === 'disbursementNotice',
    );
  }, [
    consolidatedDisbursement.capitalProviderOrganization.id,
    invoiceQuery.data?.getInvoiceById.invoiceReports,
  ]);

  const { data: metricsDataRequest } = useGetCreditFacilityMetricsQuery({
    skip: !invoiceQuery.data,
    variables: {
      creditFacilityId: invoiceQuery?.data?.getInvoiceById
        .creditFacilityId as string,
      postingDate: commonDate.currentDateStr(),
    },
  });

  const metrics = metricsDataRequest?.metrics;

  if (invoiceQuery.error) {
    throw invoiceQuery.error;
  }

  if (!invoiceQuery.data?.getInvoiceById || !invoiceLoanIds) {
    return null;
  }

  const invoice = invoiceQuery.data.getInvoiceById;

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={() => onToggle(false)}
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader mt="1.5rem" px="3rem" pb="0">
          <Typography variant="section-title" trim>
            Disbursement for{' '}
            {_.startCase(
              consolidatedDisbursement.capitalProviderOrganization.name,
            )}
          </Typography>
        </DrawerHeader>
        <Divider my="1.5rem" w="100%" />
        <DrawerBody
          width="full"
          px="3rem"
          display="flex"
          flexDirection="column"
          gap="2rem"
        >
          <HStack justifyContent="space-between" w="100%">
            {reportDownload && (
              <DisbursementNoticeDownloadButton report={reportDownload} />
            )}
            <Spacer />
            {(() => {
              if (consolidatedDisbursement.status === 'paid') {
                return (
                  <Tag variant="solid-muted" colorScheme="green">
                    <TagLeftIcon>
                      <Icon icon="circle-check" />
                    </TagLeftIcon>
                    <TagLabel>Paid</TagLabel>
                  </Tag>
                );
              }
              if (consolidatedDisbursement.status === 'canceled') {
                return (
                  <Tag variant="solid-muted" colorScheme="gray">
                    <TagLeftIcon>
                      <Icon icon="circle-x" />
                    </TagLeftIcon>
                    <TagLabel>Canceled</TagLabel>
                  </Tag>
                );
              }
              return (
                <MarkAsDisbursedButton
                  invoiceConsolidatedDisbursement={consolidatedDisbursement}
                  paymentAction={
                    consolidatedDisbursement.totalCashDisbursementAmount > 0
                      ? 'Disbursed'
                      : 'Notified'
                  } // if there is a cash disbursement, copy should say Disbursed. If only interest capitalization, copy is Notified
                  invoice={invoice}
                />
              );
            })()}
          </HStack>

          {taskId && <TaskDetails taskId={taskId} />}

          <Divider />
          <Typography variant="section-subtitle" trim>
            Net Funds Owed to{' '}
            {_.startCase(
              consolidatedDisbursement.capitalProviderOrganization.name,
            )}
          </Typography>
          <VStack w="100%" gap="1rem">
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">Lender Share</Typography>
              <Typography variant="base-muted">
                {consolidatedDisbursement.capitalProviderProRataForFacility
                  ? percentageToPercentageLabel(
                      consolidatedDisbursement.capitalProviderProRataForFacility *
                        100,
                      6,
                      true,
                    )
                  : '-'}
              </Typography>
            </HStack>
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">Funds Owed to Lender</Typography>
              <Typography variant="base-muted">
                {formatCurrencyAmount(
                  consolidatedDisbursement.totalCashDisbursementAmount,
                  Bank.Currency.USD,
                )}
              </Typography>
            </HStack>
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">Paid Date to Lender</Typography>
              <Typography variant="base-muted">
                {consolidatedDisbursement.paymentDate
                  ? commonDate.fullDateStrToHumanDateString(
                      consolidatedDisbursement.paymentDate,
                    )
                  : '-'}
              </Typography>
            </HStack>
          </VStack>

          <Divider />
          <Typography variant="section-subtitle" trim>
            Disbursement Details
          </Typography>
          <VStack w="100%" gap="1rem">
            {invoiceLoanIds.length > 0 && (
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Loan IDs</Typography>
                <Typography variant="base-muted">
                  {invoiceLoanIds
                    .map((loanId) => truncateUuid(loanId))
                    .join(', ')}
                </Typography>
              </HStack>
            )}
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">Invoice ID</Typography>
              <Typography variant="base-muted">
                {truncateUuid(invoice.id)}
              </Typography>
            </HStack>
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">
                Gross Disbursement Amount
              </Typography>
              <Typography variant="base-muted">
                {dollarNumberWithCommas(
                  consolidatedDisbursement.totalCashDisbursementAmount,
                )}
              </Typography>
            </HStack>
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">
                Disbursement Servicer Fee
              </Typography>
              <Typography variant="base-muted">
                {dollarNumberWithCommas(
                  consolidatedDisbursement.totalCashServiceFeeAmount,
                )}
              </Typography>
            </HStack>
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">
                Net Disbursement Amount
              </Typography>
              <Typography variant="base-muted">
                {dollarNumberWithCommas(
                  consolidatedDisbursement.totalPostServiceFeeCashDisbursementAmount,
                )}
              </Typography>
            </HStack>
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">
                Gross Capitalization Amount
              </Typography>
              <Typography variant="base-muted">
                {dollarNumberWithCommas(
                  consolidatedDisbursement.totalInterestCapitalizationAmount,
                ) ?? '-'}
              </Typography>
            </HStack>
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">
                Capitalization Servicer Fee
              </Typography>
              <Typography variant="base-muted">
                {dollarNumberWithCommas(
                  consolidatedDisbursement.totalInterestCapitalizationServiceFeeAmount,
                )}
              </Typography>
            </HStack>
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">
                Net Capitalization Amount
              </Typography>
              <Typography variant="base-muted">
                {dollarNumberWithCommas(
                  consolidatedDisbursement.totalPostServiceFeeInterestCapitalizationAmount,
                )}
              </Typography>
            </HStack>
          </VStack>

          <Divider />
          <Typography variant="section-subtitle" trim>
            Facility Details
          </Typography>
          <VStack w="100%" gap="1rem">
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">Name</Typography>
              <Typography variant="base-muted">
                {_.startCase(invoice.creditFacility.name)}
              </Typography>
            </HStack>
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">Borrower</Typography>
              <Typography variant="base-muted">
                {_.startCase(invoice.paymentSourceOrganization.name)}
              </Typography>
            </HStack>
            <HStack alignItems="start" w="100%">
              <Typography fontWeight="medium">
                {_.startCase(
                  consolidatedDisbursement.capitalProviderOrganization.name,
                )}{' '}
                Commitment
              </Typography>

              <Spacer />
              <Stack alignItems="end">
                <Typography variant="base-muted">
                  {formatCurrencyAmount(
                    lenderCommitmentAmount.value,
                    lenderCommitmentAmount.currency,
                  )}
                </Typography>
                <Typography variant="base-muted">
                  (
                  {percentageToPercentageLabel(
                    commonUtils.divideByOrZero(
                      consolidatedDisbursement.totalCashDisbursementAmount +
                        consolidatedDisbursement.totalInterestCapitalizationAmount,
                      invoice.totalAmount,
                    ) * 100,
                    6,
                  )}{' '}
                  Pro Rata)
                </Typography>
              </Stack>
            </HStack>
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">Global Principal</Typography>
              {metrics ? (
                <Typography variant="base-muted">
                  {formatCurrencyAmount(
                    metrics.outstandingPrincipalBalance,
                    Bank.Currency.USD,
                  )}
                </Typography>
              ) : (
                <Skeleton w="10rem" h="1rem" />
              )}
            </HStack>
          </VStack>

          <Divider />
          <Typography variant="section-subtitle" trim>
            Payment Instructions
          </Typography>
          <VStack w="100%" gap="1rem">
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">
                Destination Bank Account
              </Typography>
              <Typography variant="base-muted">
                {invoice.destinationBankAccount
                  ? getBankAccountDisplay({
                      institutionName:
                        invoice.destinationBankAccount.institutionName,
                      accountName: invoice.destinationBankAccount.name,
                      accountNumber:
                        invoice.destinationBankAccount.accountNumber,
                    })
                  : '-'}
              </Typography>
            </HStack>
          </VStack>
        </DrawerBody>

        <DrawerFooter display="flex" justifySelf="flex-end" w="100%" mb="2em">
          <Button
            as={RouterLink}
            to={`/invoices/${invoice.id}`}
            rightIcon={<Icon icon="chevron-right" />}
            variant="v2-ghost"
          >
            View Invoice
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
